// Global Instructions Rule Applied!
//
// Agent drafts — HTTP service (docs/AGENT_PROGRAMME_LP_HIGHLIGHTS_DESIGN.md
// Phase 0). Backend only; URLs from API_ROUTES; responses Zod-validated.
// Nothing here sends anything — Copy records 'copied'; Send to Outlook
// (Phase 6a) asks the backend to create the draft in the caller's own
// Outlook Drafts folder, and the backend alone talks to Graph.
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  AgentDraftInteractionRef, AgentDraftRecord, OutlookDraftCapability, validateAgentDraft, validateAgentDraftsList, validateOutlookDraftCapability
} from '../../../state_manager/data_models/agent_drafts_model.ts'
import { InteractionRecord } from '../../../state_manager/data_models/interaction_record.ts'
import { fetchInteractionsForAccount } from '../../interactions/services/interactionsService.ts'
import { readJsonBody } from '../../../core/utils/utilities.ts'

export interface AgentDraftsPage {
  drafts: AgentDraftRecord[]
  outlookCapability: OutlookDraftCapability
}

const BACKEND_BASE_URL = getBackendBaseUrl()

const jsonHeaders = (accessToken: string | null): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

const readError = async (response: Response, fallback: string): Promise<string> => {
  const body = await readJsonBody<{ error?: string; message?: string }>(response)
  return body?.message || body?.error || `${fallback}: ${response.status} ${response.statusText}`
}

export const fetchAgentDrafts = async (
  contextTable: 'accounts' | 'prospects',
  contextId: string,
  status: string[] | null,
  accessToken: string | null
): Promise<AgentDraftsPage> => {
  const query: Record<string, string> = { context_table: contextTable, context_id: contextId }
  if (status && status.length) query.status = status.join(',')
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.DRAFTS, {}, query)
  const response = await fetch(url, { method: 'GET', headers: jsonHeaders(accessToken) })
  if (!response.ok) throw new Error(await readError(response, 'Failed to load drafts'))
  const json = (await response.json()) as { data?: unknown; outlook_capability?: unknown }
  return { drafts: validateAgentDraftsList(json.data ?? []), outlookCapability: validateOutlookDraftCapability(json.outlook_capability) }
}

export const fetchMyAgentDrafts = async (status: string[] | null, accessToken: string | null, limit = 100): Promise<AgentDraftsPage> => {
  const query: Record<string, string | number> = { limit }
  if (status && status.length) query.status = status.join(',')
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.DRAFTS_MINE, {}, query)
  const response = await fetch(url, { method: 'GET', headers: jsonHeaders(accessToken) })
  if (!response.ok) throw new Error(await readError(response, 'Failed to load drafts'))
  const json = (await response.json()) as { data?: unknown; outlook_capability?: unknown }
  return { drafts: validateAgentDraftsList(json.data ?? []), outlookCapability: validateOutlookDraftCapability(json.outlook_capability) }
}

/** Send to Outlook. Resolves to the updated draft (status 'exported'); rejects
 *  with the backend's own sentence when a guard blocked it, the mailbox is not
 *  enabled, or Graph refused — the draft stays proposed in every failure. */
export const exportAgentDraftToOutlook = async (draftId: string, accessToken: string | null): Promise<AgentDraftRecord> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.DRAFT_EXPORT_OUTLOOK, { draftId })
  const response = await fetch(url, { method: 'POST', headers: jsonHeaders(accessToken) })
  if (!response.ok) throw new Error(await readError(response, 'Failed to create the Outlook draft'))
  const json = (await response.json()) as { data?: unknown }
  return validateAgentDraft(json.data)
}

export const setAgentDraftStatus = async (
  draftId: string,
  status: 'accepted' | 'dismissed' | 'proposed',
  accessToken: string | null,
  options: { reason?: string | null; acceptedAction?: 'copied' | null } = {}
): Promise<void> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.DRAFT_STATUS, { draftId })
  const response = await fetch(url, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: { status, reason: options.reason ?? null, accepted_action: options.acceptedAction ?? null } })
  })
  if (!response.ok) throw new Error(await readError(response, 'Failed to update draft'))
}

/** Phase 6b fuzzy tier: the user's Confirm (accept=true → status 'sent') or
 *  Not this (accept=false → the candidate is rejected for good) on the sent
 *  candidate the sweep offered. Resolves to the updated draft. */
export const confirmAgentDraftCandidate = async (draftId: string, accept: boolean, accessToken: string | null): Promise<AgentDraftRecord> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.DRAFT_SENT_CANDIDATE, { draftId })
  const response = await fetch(url, { method: 'POST', headers: jsonHeaders(accessToken), body: JSON.stringify({ data: { accept } }) })
  if (!response.ok) throw new Error(await readError(response, accept ? 'Failed to confirm the sent email' : 'Failed to reject the candidate'))
  const json = (await response.json()) as { data?: unknown }
  return validateAgentDraft(json.data)
}

/** Phase 6b manual tier: Mark as sent with an interaction of the draft's
 *  account. Resolves to the updated draft (status 'sent'). */
export const markAgentDraftSent = async (draftId: string, interactionId: string, accessToken: string | null): Promise<AgentDraftRecord> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.DRAFT_MARK_SENT, { draftId })
  const response = await fetch(url, { method: 'POST', headers: jsonHeaders(accessToken), body: JSON.stringify({ data: { interaction_id: interactionId } }) })
  if (!response.ok) throw new Error(await readError(response, 'Failed to mark the draft as sent'))
  const json = (await response.json()) as { data?: unknown }
  return validateAgentDraft(json.data)
}

/** The Mark as sent picker: the account's recent interactions through the
 *  existing account-interactions read (no new RPC), reduced to the ref shape
 *  the card renders. Newest first. */
export const fetchAgentDraftInteractionOptions = async (accountId: string, accessToken: string | null, limit = 50): Promise<AgentDraftInteractionRef[]> => {
  const rows = await fetchInteractionsForAccount(accountId, {}, accessToken, { limit, offset: 0 })
  const pick = (r: InteractionRecord, key: string): string | null => {
    const v = (r as unknown as Record<string, unknown>)[key]
    return typeof v === 'string' ? v : null
  }
  return rows
    .map(
      (r): AgentDraftInteractionRef => ({
        interactions__id: pick(r, 'interactions__id') ?? '',
        interactions__subject: pick(r, 'interactions__subject'),
        interactions__datetime: pick(r, 'interactions__datetime'),
        interactions__to_names: null
      })
    )
    .filter((r) => r.interactions__id)
    .sort((a, b) => (b.interactions__datetime ?? '').localeCompare(a.interactions__datetime ?? ''))
}
